/**
 * Central filter-defaults registry (ER-10190 / ER-10192).
 *
 * Single source of truth for persisted table filter keys. `getFilters(key)` returns
 * these values when the current Access has nothing saved for that key (idea-MDP-526 On).
 *
 * Values mirror current product UI defaults. Preserve storage key spellings,
 * including historical typos (`rediness*`).
 */

export const FILTER_DEFAULTS = Object.freeze({
  // Organization > Positions
  organizationPositions: Object.freeze(["Draft", "Active"]),

  // Organization > Employees table
  employeesOrganizationTable: Object.freeze(["draft", "active"]),

  // Organization > Employees (legacy EmployeeView default includes No Archived)
  // ER-11658: was the label string "No Archived", which matches no label and no stable
  // value anywhere and was therefore already inert. Value-only per the Archived group's
  // stable values (no_archived / show_archived).
  organizationEmployees: Object.freeze(["no_archived"]),

  // Readiness employees lists
  readinessEmployees: Object.freeze([]),

  // Readiness > Responsibilities directory (not Organization > Responsibilities)
  readinessResponsibilities: Object.freeze([]),

  // Readiness checklist (path-to-readiness checklist items)
  readinessChecklist: Object.freeze([]),

  // Organization > Roles (shared with position-attached roles)
  readinessRolesData: Object.freeze(["draft", "active"]),

  // Organization > Responsibilities
  organizationResponsibilities: Object.freeze(["draft", "active"]),

  // Organization > Departments
  organizationDepartmentsData: Object.freeze(["draft", "active"]),

  // Organization > Job Families
  organizationJobFamilies: Object.freeze(["draft", "active"]),

  // Organization > Job Categories (ER-12357: default-aware empty-account check)
  organizationJobCategories: Object.freeze(["draft", "active"]),

  // Responsibility checklist (typo key preserved)
  redinessResponsibilityChecklist: Object.freeze(["active", "draft"]),

  // Responsibility assignments
  readinessResponsibilityAssignment: Object.freeze(["active"]),
  redinessResponsibilityAssigment: Object.freeze(["active"]),

  // Settings > Users
  organizationSettings: Object.freeze(["has access", "invited"]),

  // Role > Responsibilities
  RoleResponsibilities: Object.freeze([]),

  // Responsibility > Edit checkpoints
  editCheckpointsTable: Object.freeze(["active", "draft"]),

  // Employee badge holders
  EmployeeBadgesHolderList: Object.freeze(["Current"]),

  // Mastered responsibilities
  MasterResponsibilities: Object.freeze([]),

  // Responsibility badge holders (typo key preserved)
  redinessResponsibilityBadgesHolder: Object.freeze([]),

  // Responsibility evaluation
  ResponsibilityEvaluationView: Object.freeze([]),

  // Coach inspection badge filters
  inspectionBadgeFilters: Object.freeze([]),

  // Operating currencies
  operatingCurrenciesTable: Object.freeze(["active"]),

  // Pay ranges
  payRangeList: Object.freeze(["draft", "active"]),

  // Fair Pay → Pay Plans list (no filters on first load)
  payPlansList: Object.freeze([]),

  // Fair Pay → Employees list (Base Pay tab)
  fairPayEmployeesList: Object.freeze([]),

  // Admin accounts (Pinia key)
  accountsList: Object.freeze([]),

  // Currency configuration table (prefix + topLevel)
  "currency-filter-currency": Object.freeze(["active"]),

  // Scorecard KPIs (typo key preserved)
  redinessScorecardKpis: Object.freeze(["active", "draft"]),

  // Legacy top-level localStorage keys (migrated onto helper)
  appliedFilters: Object.freeze(["draft", "open"]),
  appliedFiltersScore: Object.freeze(["draft", "active"]),
  appliedFiltersPeriod: Object.freeze(["draft", "active"]),
  userAppliedFilter: Object.freeze(["invited", "operational"]),

  // Dynamic / tree attachment keys (empty product default)
  readinessrole: Object.freeze([]),
  readinessresponsibility: Object.freeze([]),
  readinessknowledge: Object.freeze([]),
  organizationPositionRoleAttachmentrole: Object.freeze([]),
  readinessCatalogsRoleAttachmentresponsibility: Object.freeze([]),
  employeeAssignmentsposition: Object.freeze([]),

  // Surfaces that may persist later (UI defaults registered for consistency)
  operatingCountryTable: Object.freeze(["active"]),
  responsibilityBadgeHolders: Object.freeze(["Current"]),
});

/**
 * Departments filter modal radio defaults (ER-10685). These are NOT persisted values: the
 * "No Filter" / "Do Not Show" selections are the absence of an entry in the
 * `organizationDepartmentsData` array, so they are registered here rather than in it.
 */
export const DEPARTMENT_RADIO_FILTER_DEFAULTS = Object.freeze({
  archived: "Do Not Show",
  manager: "No Filter",
  positions: "No Filter",
});

/** Top-level localStorage keys historically used outside exlAppData.filters. */
export const LEGACY_TOP_LEVEL_FILTER_KEYS = Object.freeze([
  "appliedFilters",
  "appliedFiltersScore",
  "appliedFiltersPeriod",
  "userAppliedFilter",
]);

/**
 * @param {string} key - Persisted filter storage key
 * @returns {string[]|null} Copy of defaults, or null if key is not registered
 */
export function getDefaultFiltersForKey(key) {
  if (!Object.prototype.hasOwnProperty.call(FILTER_DEFAULTS, key)) {
    return null;
  }
  return [...FILTER_DEFAULTS[key]];
}

const normalizeFilterValue = (filter) => {
  const raw = filter && typeof filter === "object" && "value" in filter ? filter.value : filter;
  return String(raw ?? "")
    .trim()
    .toLowerCase();
};

/**
 * "No filters applied" check that is aware of a list's default filters (ER-12353).
 * Lists pre-select defaults (e.g. Draft + Active), so `values.length === 0` is never true
 * for an untouched list. This returns true when `values` is empty, or when it holds exactly
 * the defaults registered for `key` (order- and case-insensitive; `{ value }` objects are
 * unwrapped). Any extra or missing filter, or an unregistered key, returns false.
 *
 * @param {string} key - Persisted filter storage key (see FILTER_DEFAULTS)
 * @param {Array<string|{value: string}>} values - Currently applied filters
 * @returns {boolean}
 */
export function areDefaultFiltersApplied(key, values) {
  if (!Array.isArray(values) || values.length === 0) {
    return true;
  }
  const defaults = getDefaultFiltersForKey(key);
  if (!defaults) {
    return false;
  }
  const applied = new Set(values.map(normalizeFilterValue));
  const expected = new Set(defaults.map(normalizeFilterValue));
  return applied.size === expected.size && [...applied].every((value) => expected.has(value));
}

/** Registered storage keys (for tests / enumeration). */
export function getRegisteredFilterKeys() {
  return Object.keys(FILTER_DEFAULTS);
}
